import * as Ariakit from '@ariakit/react'
import { FC } from 'react'

import { Pencil } from '@syconium/little-miss-figgy'

import { useTranslation } from '../../../../../../app/_providers/TranslationProvider.client'
import { LogoFile } from '../../../../../../containers/EmbroideryContainer/types'

import { LogoChoice } from './logoChoices'
import {
  AddNewLogoButton,
  AddNewLogoLabel,
  AddNewLogoPrice,
  DigitizationBadge,
  EditLogoButton,
  LOGO_TILE_WIDTH,
  LogoGrid,
  LogoImage,
  LogoTile,
  LogoTileButton,
} from './styles'

export type LogoPickerProps = {
  /** Names the group for assistive tech; the visible heading sits above the picker. */
  labelId: string
  choices: readonly LogoChoice[]
  selectedLogoId: string | undefined
  onChooseLogo(logoFile: LogoFile): void
  /** The logo whose tile gets a pencil, which goes back to its upload form. */
  editableLogoId?: string
  onEditLogo(): void
  /** Ends the grid with an "Add New Logo" tile, for when the order may still take a new logo. */
  canAddNewLogo: boolean
  /** The digitization charge, already formatted. */
  setupPriceLabel: string | undefined
  onAddNewLogo(): void
}

/**
 * The logos a garment can take, as a grid of tiles to choose one from, and the tile that starts a
 * new upload when the order still allows one.
 */
export const LogoPicker: FC<LogoPickerProps> = ({
  labelId,
  choices,
  selectedLogoId,
  onChooseLogo,
  editableLogoId,
  onEditLogo,
  canAddNewLogo,
  setupPriceLabel,
  onAddNewLogo,
}) => {
  const {
    embroidery: {
      modal: {
        forms: { dtcLogoEmbroideryForm: copy, logoFields },
      },
    },
  } = useTranslation()

  return (
    <Ariakit.RadioProvider value={selectedLogoId ?? null}>
      <Ariakit.RadioGroup aria-labelledby={labelId} render={<LogoGrid />}>
        {choices.map(({ logoFile, status }) => {
          const isPending = status === 'pending'
          const isSelected = logoFile.id === selectedLogoId

          return (
            <LogoTile key={logoFile.id} $selected={isSelected} $disabled={isPending}>
              <Ariakit.Radio
                accessibleWhenDisabled
                disabled={isPending}
                value={logoFile.id}
                onChange={() => onChooseLogo(logoFile)}
                render={
                  <LogoTileButton type='button'>
                    <LogoImage
                      $disabled={isPending}
                      src={logoFile.preview}
                      alt={logoFile.name}
                      aspectRatios={{ sm: 1, md: 1 }}
                      widths={{ unit: 'px', sm: LOGO_TILE_WIDTH, md: LOGO_TILE_WIDTH }}
                      loading='lazy'
                      transparentBackground
                    />
                    {status === 'new' && (
                      <DigitizationBadge>{copy.digitizationRequest}</DigitizationBadge>
                    )}
                    {isPending && <DigitizationBadge>{copy.digitizationPending}</DigitizationBadge>}
                  </LogoTileButton>
                }
              />

              {logoFile.id === editableLogoId && (
                <EditLogoButton type='button' aria-label={logoFields.editLogo} onClick={onEditLogo}>
                  <Pencil height='20px' width='20px' />
                </EditLogoButton>
              )}
            </LogoTile>
          )
        })}

        {canAddNewLogo && (
          <AddNewLogoButton type='button' onClick={onAddNewLogo}>
            <AddNewLogoLabel>{copy.addNewLogo}</AddNewLogoLabel>
            {setupPriceLabel !== undefined && <AddNewLogoPrice>+{setupPriceLabel}</AddNewLogoPrice>}
          </AddNewLogoButton>
        )}
      </Ariakit.RadioGroup>
    </Ariakit.RadioProvider>
  )
}
